/**
 * TerrainMaterial - PBR material with tri-planar texture mapping
 * 
 * Uses DataArrayTextures for efficient multi-material rendering.
 * Supports smooth blending between materials at vertex level.
 */

import * as THREE from 'three';
import {
  MATERIAL_ROUGHNESS_MULTIPLIER,
  MATERIAL_METALNESS_OFFSET,
  MATERIAL_AO_INTENSITY,
  MATERIAL_NORMAL_STRENGTH,
  MATERIAL_REPEAT_SCALES,
  FOLIAGE_TRANSLUCENCY,
  FOLIAGE_SKY_BIAS,
} from '@worldify/shared';
import { useGameStore } from '../../state/store';
import { textureCache } from './TextureCache.js';
import { getMaterialPallet } from './MaterialPallet.js';
import {
  TERRAIN_MATERIAL_REPEAT_SCALE,
  TERRAIN_MATERIAL_BLEND_OFFSET_RAD,
  TERRAIN_LOD_TEX_FIRST_LOD,
  TERRAIN_LOD_TEX_PER_LOD,
} from './constants.js';
import { createDefaultPlaceholders, loadPalletPlaceholders } from './PlaceholderTextures.js';
import { WaterMaterial, getWaterMaterial, updateWaterTime } from './WaterMaterial.js';
import {
  terrainVertexPrefix,
  terrainVertexSuffix,
  terrainFragmentPrefix,
  terrainDiffuseFragment,
  terrainRoughnessFragment,
  terrainMetalnessFragment,
  terrainAoFragment,
  terrainNormalFragment,
  terrainLightFragment,
  terrainDebugFragment,
  depthVertexPrefix,
  depthVertexSuffix,
  depthFragmentPrefix,
  depthAlphaDiscard,
} from './terrainShaders.js';

export type TextureResolution = 'low' | 'high';

/** Alpha threshold for shadow depth testing on transparent materials */
const ALPHA_CUTOFF = 0.5;

// Wind animation parameters
const WIND_STRENGTH = 0.1;
const WIND_SPEED = 0.7;
const WIND_FREQUENCY = 1.0;

export interface LoadedTextures {
  albedo: THREE.DataArrayTexture;
  normal: THREE.DataArrayTexture;
  ao: THREE.DataArrayTexture;
  roughness: THREE.DataArrayTexture;
  metalness: THREE.DataArrayTexture;
}

// ============== Texture Loading ==============

/**
 * Load texture binary data, using cache if available.
 * Supports per-file download progress via ReadableStream.
 */
async function loadTextureData(
  resolution: TextureResolution,
  mapType: string,
  version: string,
  baseUrl: string,
  onFileProgress?: (received: number, total: number) => void
): Promise<Uint8Array> {
  const cached = await textureCache.getTexture(resolution, mapType, version);
  
  if (cached) {
    console.log(`Cache hit: Loaded ${resolution}/${mapType} from IndexedDB [${version}]`);
    return new Uint8Array(cached);
  }

  console.log(`Cache miss: Fetching ${resolution}/${mapType} from network`);
  
  const response = await fetch(`${baseUrl}/${resolution}/${mapType}.bin`, {
    headers: { 'Content-Type': 'application/octet-stream' },
  });
  
  if (!response.ok) {
    throw new Error(`Failed to fetch ${resolution}/${mapType}: ${response.status}`);
  }
  
  let result: Uint8Array;
  
  // Use ReadableStream for per-file download progress
  const contentLength = parseInt(response.headers.get('Content-Length') || '0', 10);
  if (contentLength > 0 && response.body && onFileProgress) {
    const reader = response.body.getReader();
    const chunks: Uint8Array[] = [];
    let receivedBytes = 0;
    
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      chunks.push(value);
      receivedBytes += value.length;
      onFileProgress(receivedBytes, contentLength);
    }
    
    // Combine chunks into a single Uint8Array
    result = new Uint8Array(receivedBytes);
    let offset = 0;
    for (const chunk of chunks) {
      result.set(chunk, offset);
      offset += chunk.length;
    }
  } else {
    result = new Uint8Array(await response.arrayBuffer());
  }
  
  // Fire-and-forget: don't block texture creation on IndexedDB write
  const sizeMB = (result.byteLength / (1024 * 1024)).toFixed(2);
  textureCache.saveTexture(resolution, mapType, result.buffer as ArrayBuffer, version).then(
    () => console.log(`Cached ${resolution}/${mapType} (${sizeMB} MB) [${version}]`),
    (err) => console.warn(`Failed to cache ${resolution}/${mapType}:`, err)
  );
  
  return result;
}

/**
 * Create a DataArrayTexture from loaded binary data.
 */
function createDataArrayTexture(
  data: Uint8Array,
  width: number,
  height: number,
  layers: number,
  channels: string,
  resolution: TextureResolution,
  mapType: string
): THREE.DataArrayTexture {
  const channelCount = channels.length;
  const texture = new THREE.DataArrayTexture(data, width, height, layers);
  
  texture.format = channelCount === 4 ? THREE.RGBAFormat : THREE.RedFormat;
  texture.type = THREE.UnsignedByteType;
  
  // Albedo is sRGB and is left WITHOUT an explicit internalFormat so three uploads it as
  // SRGB8_ALPHA8 and the sampler decodes it in hardware (WebGL2 supports it on texture arrays):
  // the shader used to run a pow() curve per fragment for this. Every other map is linear data.
  if (mapType === 'albedo') {
    texture.colorSpace = THREE.SRGBColorSpace;
  } else {
    texture.internalFormat = channelCount === 4 ? 'RGBA8' : 'R8';
    texture.colorSpace = THREE.LinearSRGBColorSpace;
  }
  
  if (resolution === 'low') {
    texture.minFilter = THREE.NearestMipmapLinearFilter;
    texture.magFilter = THREE.NearestFilter;
    texture.anisotropy = 1;
  } else {
    texture.minFilter = THREE.LinearMipmapLinearFilter;
    texture.magFilter = THREE.LinearFilter;
    texture.anisotropy = 8;
  }
  
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.generateMipmaps = true;
  texture.needsUpdate = true;
  
  return texture;
}

/**
 * Load all textures for a given resolution.
 */
export async function loadDataArrayTextures(
  resolution: TextureResolution,
  onProgress?: (loaded: number, total: number) => void
): Promise<LoadedTextures> {
  const mapTypes = ['albedo', 'normal', 'ao', 'roughness', 'metalness'] as const;
  const pallet = getMaterialPallet();
  const version = await textureCache.getLatestVersion();
  const baseUrl = await textureCache.getLatestMaterialUrl();
  
  const textures: Partial<LoadedTextures> = {};
  let completed = 0;
  const total = mapTypes.length;
  
  for (const mapType of mapTypes) {
    const meta = pallet.maps[resolution][mapType];
    if (!meta) {
      throw new Error(`Missing metadata for ${resolution}/${mapType}`);
    }
    
    const textureData = await loadTextureData(resolution, mapType, version, baseUrl, (received, fileTotal) => {
      // Smooth progress: completed maps + fraction of current file
      const fileProgress = fileTotal > 0 ? received / fileTotal : 0;
      onProgress?.(completed + fileProgress, total);
    });
    textures[mapType] = createDataArrayTexture(
      textureData,
      meta.width,
      meta.height,
      meta.layers,
      meta.channels,
      resolution,
      mapType
    );
    
    completed++;
    onProgress?.(completed, total);
  }
  
  return textures as LoadedTextures;
}

// ============== Shader Utilities ==============

interface ShaderWithUniforms {
  uniforms: Record<string, { value: unknown }>;
  vertexShader: string;
  fragmentShader: string;
  defines?: Record<string, string | number | boolean>;
}

function createBlendOffsetMatrix(): THREE.Matrix3 {
  const rad = TERRAIN_MATERIAL_BLEND_OFFSET_RAD;
  const euler = new THREE.Euler(rad, rad, rad, 'XYZ');
  const quat = new THREE.Quaternion().setFromEuler(euler);
  const mat4 = new THREE.Matrix4().makeRotationFromQuaternion(quat);
  return new THREE.Matrix3().setFromMatrix4(mat4);
}

// ============== TerrainMaterial Class ==============

// ============== Per-LOD texture scale (shared uniform objects) ==============
/**
 * ONE uniform object per value, handed to every material's uniform set BY REFERENCE, so a single write
 * reaches every terrain program — including the LOD-debug variants and the held-item materials, which
 * nothing else knows to update.
 */
const uTexScaleFirstLod = { value: TERRAIN_LOD_TEX_FIRST_LOD };
const uTexScalePerLod = { value: TERRAIN_LOD_TEX_PER_LOD };

export class TerrainMaterial extends THREE.MeshStandardMaterial {
  private _shader: ShaderWithUniforms | null = null;
  private textures: LoadedTextures | null = null;
  private _windSpeed: number = WIND_SPEED;
  // Held on the instance, not only in the uniform: a setter called before the first compile has no
  // `_shader` to write to, and a LOD variant is configured the moment it is constructed.
  private _debugMode: number = 0;
  /** Live uniform; the shader takes the three-squarings path while it is exactly 8 (a define). */
  private _blendSharpness: number = 8.0;
  private readonly _lodColor = new THREE.Color(0xffffff);
  
  // Quality-driven shader defines
  static qualityNormalMaps = true;
  static qualityAoMaps = true;
  static qualityMetalnessMaps = true;
  
  /**
   * Override customProgramCacheKey so that Three.js's program cache
   * treats different quality-define combinations as distinct programs.
   * Without this, toggling defines inside onBeforeCompile has no effect
   * because the cache key (based on onBeforeCompile.toString()) never
   * changes, so Three.js returns the old cached program and skips
   * onBeforeCompile entirely.
   */
  customProgramCacheKey(): string {
    return [
      super.customProgramCacheKey(),
      TerrainMaterial.qualityNormalMaps ? 'N' : 'n',
      TerrainMaterial.qualityAoMaps ? 'A' : 'a',
      TerrainMaterial.qualityMetalnessMaps ? 'M' : 'm',
      this._debugMode > 0 ? 'X' : 'x',            // TERRAIN_DEBUG: the debug views compile in
      this._blendSharpness === 8 ? 'S' : 's',     // TRIPLANAR_SHARPNESS_8
    ].join('|');
  }
  
  constructor(isTransparent: boolean = false) {
    super({
      roughness: 1.0,
      metalness: 1.0,
      // NOTE: Do NOT set transparent: true for alpha-tested materials.
      // alphaTest uses a hard cutoff (discard), not blending, so fragments that
      // survive are effectively opaque and must write to the depth buffer.
      // transparent: true causes depthWrite: false by default in Three.js, which
      // breaks depth ordering for large merged meshes (batched terrain groups).
      transparent: false,
      side: isTransparent ? THREE.DoubleSide : THREE.FrontSide,
      alphaTest: isTransparent ? ALPHA_CUTOFF : 0,
    });
    
    this.setTextures(createDefaultPlaceholders());
    
    this.onBeforeCompile = (shader) => {
      this._shader = shader;
      
      shader.uniforms.mapArray = { value: this.textures?.albedo };
      shader.uniforms.normalArray = { value: this.textures?.normal };
      shader.uniforms.aoArray = { value: this.textures?.ao };
      shader.uniforms.roughnessArray = { value: this.textures?.roughness };
      shader.uniforms.metalnessArray = { value: this.textures?.metalness };
      shader.uniforms.repeatScale = { value: TERRAIN_MATERIAL_REPEAT_SCALE };
      shader.uniforms.repeatScales = { value: MATERIAL_REPEAT_SCALES };
      shader.uniforms.uTexScaleFirstLod = uTexScaleFirstLod;
      shader.uniforms.uTexScalePerLod = uTexScalePerLod;
      shader.uniforms.blendOffset = { value: createBlendOffsetMatrix() };
      shader.uniforms.debugMode = { value: this._debugMode };
      shader.uniforms.uLodColor = { value: this._lodColor };
      
      // Material adjustment uniforms - use shared constants for pallet viewer consistency
      shader.uniforms.roughnessMultiplier = { value: MATERIAL_ROUGHNESS_MULTIPLIER };
      shader.uniforms.metalnessOffset = { value: MATERIAL_METALNESS_OFFSET };
      shader.uniforms.aoIntensity = { value: MATERIAL_AO_INTENSITY };
      shader.uniforms.normalStrength = { value: MATERIAL_NORMAL_STRENGTH };
      shader.uniforms.blendSharpness = { value: this._blendSharpness };
      
      // Voxel light fill curve exponents (sky lifts at <1, block tightens at >1)
      shader.uniforms.skyFillPower = { value: 0.5 };
      shader.uniforms.blockFillPower = { value: 2.0 };

      // Block-light (emitter) uniforms — defaults mirror DEFAULT_ENVIRONMENT
      // (blockLightColor '#ffb050', blockLightIntensity 1.5); live-tuned via
      // applyBlockLightSettings from the debug panel.
      shader.uniforms.uBlockLightColor = { value: new THREE.Color(0xffd397) };
      shader.uniforms.uBlockLightIntensity = { value: 4.0 };

      // Quality-driven shader defines
      // CRITICAL: shader.defines is a direct reference to material.defines.
      // We must explicitly DELETE defines when they should be off, otherwise
      // they persist across recompilations (needsUpdate = true) and the
      // shader still sees them even after the quality flag was turned off.
      shader.defines = shader.defines || {};
      if (TerrainMaterial.qualityNormalMaps) {
        shader.defines.QUALITY_NORMAL_MAPS = '';
      } else {
        delete shader.defines.QUALITY_NORMAL_MAPS;
      }
      if (TerrainMaterial.qualityAoMaps) {
        shader.defines.QUALITY_AO_MAPS = '';
      } else {
        delete shader.defines.QUALITY_AO_MAPS;
      }
      if (TerrainMaterial.qualityMetalnessMaps) {
        shader.defines.QUALITY_METALNESS_MAPS = '';
      } else {
        delete shader.defines.QUALITY_METALNESS_MAPS;
      }
      // The debug views are a dozen branches on a uniform int, each sampling again; they exist in the
      // program only while a debug mode is on (toggling recompiles — see customProgramCacheKey).
      if (this._debugMode > 0) {
        shader.defines.TERRAIN_DEBUG = '';
      } else {
        delete shader.defines.TERRAIN_DEBUG;
      }
      if (this._blendSharpness === 8) {
        shader.defines.TRIPLANAR_SHARPNESS_8 = '';
      } else {
        delete shader.defines.TRIPLANAR_SHARPNESS_8;
      }
      
      if (isTransparent) {
        shader.defines.USE_TEXTURE_ALPHA = '';
        shader.defines.USE_WIND = '';
        shader.uniforms.uTime = { value: 0.0 };
        shader.uniforms.uWindStrength = { value: WIND_STRENGTH };
        shader.uniforms.uWindFrequency = { value: WIND_FREQUENCY };
        // FOLIAGE_LIGHTING — wrap lighting + sky-biased hemisphere (foliageLightsFragment).
        shader.defines.FOLIAGE_LIGHTING = '';
        shader.uniforms.uFoliageTranslucency = { value: this._foliageTranslucency };
        shader.uniforms.uFoliageSkyBias = { value: this._foliageSkyBias };
      }
      
      // Vertex shader modifications
      shader.vertexShader = terrainVertexPrefix + shader.vertexShader;
      shader.vertexShader = shader.vertexShader.replace(
        '#include <worldpos_vertex>',
        `#include <worldpos_vertex>\n${terrainVertexSuffix}`
      );
      
      // Fragment shader modifications
      shader.fragmentShader = terrainFragmentPrefix + shader.fragmentShader;
      shader.fragmentShader = shader.fragmentShader.replace(
        'vec4 diffuseColor = vec4( diffuse, opacity );',
        terrainDiffuseFragment
      );
      shader.fragmentShader = shader.fragmentShader.replace(
        '#include <roughnessmap_fragment>',
        terrainRoughnessFragment
      );
      shader.fragmentShader = shader.fragmentShader.replace(
        '#include <metalnessmap_fragment>',
        terrainMetalnessFragment
      );
      shader.fragmentShader = shader.fragmentShader.replace(
        '#include <aomap_fragment>',
        terrainAoFragment
      );
      if (isTransparent) {
        // The light loop is a three chunk expanded AFTER onBeforeCompile, so to change two lines of
        // it the chunk is inlined here with those lines changed — see foliageLightsFragment.
        shader.fragmentShader = shader.fragmentShader.replace(
          '#include <lights_fragment_begin>',
          foliageLightsFragment(),
        );
      }
      shader.fragmentShader = shader.fragmentShader.replace(
        '#include <normal_fragment_maps>',
        terrainNormalFragment
      );
      shader.fragmentShader = shader.fragmentShader.replace(
        '#include <opaque_fragment>',
        terrainLightFragment
      );
      shader.fragmentShader = shader.fragmentShader.replace(
        '#include <dithering_fragment>',
        terrainDebugFragment
      );
    };
  }
  
  setTextures(textures: LoadedTextures): void {
    this.textures = textures;
    if (this._shader) {
      this._shader.uniforms.mapArray.value = textures.albedo;
      this._shader.uniforms.normalArray.value = textures.normal;
      this._shader.uniforms.aoArray.value = textures.ao;
      this._shader.uniforms.roughnessArray.value = textures.roughness;
      this._shader.uniforms.metalnessArray.value = textures.metalness;
      this.needsUpdate = true;
    }
  }

  /** Current loaded textures (used to seed dedicated held-item materials). */
  getTextures(): LoadedTextures | null {
    return this.textures;
  }

  setRepeatScale(scale: number): void {
    if (this._shader) {
      this._shader.uniforms.repeatScale.value = scale;
      this.needsUpdate = true;
    }
  }
  
  setDebugMode(mode: number): void {
    const wasOn = this._debugMode > 0;
    this._debugMode = mode;
    if (this._shader) {
      this._shader.uniforms.debugMode.value = mode;
    }
    // Crossing off<->on changes the TERRAIN_DEBUG define: recompile.
    if (wasOn !== mode > 0) this.needsUpdate = true;
  }

  /** Colour this material paints in TERRAIN_DEBUG_MODES.LOD (live uniform; no recompile). */
  setLodColor(hex: number): void {
    this._lodColor.setHex(hex);
  }
  
  setWindTime(time: number): void {
    if (this._shader?.uniforms.uTime) {
      this._shader.uniforms.uTime.value = time * this._windSpeed;
    }
  }
  
  // ============== Material Adjustment Setters ==============
  
  setRoughnessMultiplier(value: number): void {
    if (this._shader) {
      this._shader.uniforms.roughnessMultiplier.value = value;
    }
  }
  
  setMetalnessOffset(value: number): void {
    if (this._shader) {
      this._shader.uniforms.metalnessOffset.value = value;
    }
  }
  
  setAoIntensity(value: number): void {
    if (this._shader) {
      this._shader.uniforms.aoIntensity.value = value;
    }
  }

  private _foliageTranslucency = FOLIAGE_TRANSLUCENCY;
  private _foliageSkyBias = FOLIAGE_SKY_BIAS;

  /** Transparent layer only: light transmitted through back faces (0 = off). */
  setFoliageTranslucency(value: number): void {
    this._foliageTranslucency = value;
    if (this._shader?.uniforms.uFoliageTranslucency) this._shader.uniforms.uFoliageTranslucency.value = value;
  }

  /** Transparent layer only: 0 = undersides see the ground colour, 1 = weighted like tops. */
  setFoliageSkyBias(value: number): void {
    this._foliageSkyBias = value;
    if (this._shader?.uniforms.uFoliageSkyBias) this._shader.uniforms.uFoliageSkyBias.value = value;
  }
  
  setSkyFillPower(value: number): void {
    if (this._shader) {
      this._shader.uniforms.skyFillPower.value = value;
    }
  }

  setBlockFillPower(value: number): void {
    if (this._shader) {
      this._shader.uniforms.blockFillPower.value = value;
    }
  }

  setBlockLightColor(hex: string): void {
    if (this._shader) {
      (this._shader.uniforms.uBlockLightColor.value as THREE.Color).set(hex);
    }
  }

  setBlockLightIntensity(value: number): void {
    if (this._shader) {
      this._shader.uniforms.uBlockLightIntensity.value = value;
    }
  }

  setNormalStrength(value: number): void {
    if (this._shader) {
      this._shader.uniforms.normalStrength.value = value;
    }
  }
  
  setBlendSharpness(value: number): void {
    const wasDefault = this._blendSharpness === 8;
    this._blendSharpness = value;
    if (this._shader) {
      this._shader.uniforms.blendSharpness.value = value;
    }
    // Leaving or returning to 8 changes the TRIPLANAR_SHARPNESS_8 define: recompile.
    if (wasDefault !== (value === 8)) this.needsUpdate = true;
  }
  
  setWindStrength(value: number): void {
    if (this._shader?.uniforms.uWindStrength) {
      this._shader.uniforms.uWindStrength.value = value;
    }
  }
  
  setWindFrequency(value: number): void {
    if (this._shader?.uniforms.uWindFrequency) {
      this._shader.uniforms.uWindFrequency.value = value;
    }
  }
  
  setWindSpeed(value: number): void {
    this._windSpeed = value;
  }
}

// ============== Debug Mode Constants ==============

export const TERRAIN_DEBUG_MODES = {
  OFF: 0,
  VOXEL_LIGHT: 1,
  ALBEDO: 2,
  NORMAL: 3,
  AO: 4,
  ROUGHNESS: 5,
  METALNESS: 6,
  TRI_BLEND: 7,
  MATERIAL_IDS: 8,
  MATERIAL_WEIGHTS: 9,
  WORLD_NORMAL: 10,
  MATERIAL_HUE: 11,
  BLOCK_LIGHT: 12,
  LOD: 13,
  SHADING_NORMAL: 14,
} as const;

export type TerrainDebugMode = typeof TERRAIN_DEBUG_MODES[keyof typeof TERRAIN_DEBUG_MODES];

/**
 * FOLIAGE LIGHTING — three's `lights_fragment_begin` with two lines changed, for the transparent
 * (leaves) layer only. Leaves are thin: the sun on the top face lights the underside too, and a leaf's
 * underside sees sky through the canopy, not the ground. Without this the back face of a double-sided
 * leaf (three flips its normal) had N·L ≤ 0 under an overhead sun — zero direct light — and the
 * hemisphere weighted a down-facing normal toward the GROUND colour: undersides read black.
 *
 *  1. After three has multiplied the directional light by its shadow, add TRANSMISSION:
 *     `uFoliageTranslucency · saturate(dot(−N, L)) · lightColor · albedo`. Shadowed, so a leaf deep in
 *     the canopy stays dark and one at the edge glows.
 *  2. The hemisphere sample uses `foliageHemiNormal` (terrainShaders.foliageLightingDecl), whose y is
 *     pulled toward |y| by `uFoliageSkyBias`.
 *
 * Inlined because the chunk is expanded after onBeforeCompile; the anchors are asserted so a three
 * upgrade that rewords either line fails loudly here instead of silently losing the effect.
 */
function foliageLightsFragment(): string {
  const chunk = THREE.ShaderChunk.lights_fragment_begin;
  const declare = 'IncidentLight directLight;';
  const reDirect = 'RE_Direct( directLight, geometryPosition, geometryNormal, geometryViewDir, geometryClearcoatNormal, material, reflectedLight );';
  const hemi = 'getHemisphereLightIrradiance( hemisphereLights[ i ], geometryNormal )';
  // `get*LightInfo( ..., directLight );` — the line that fills directLight for each light type, BEFORE
  // three multiplies the shadow into its colour.
  const lightInfo = /get\w+LightInfo\( [^;]*directLight \);/g;
  if (!chunk.includes(declare) || !chunk.includes(reDirect) || !chunk.includes(hemi) || !lightInfo.test(chunk)) {
    throw new Error('foliageLightsFragment: three lights_fragment_begin changed — update the anchors');
  }
  // UNSHADOWED. The wrap term reads the light's colour as it was before the shadow multiply, so the
  // shaded side of a canopy still receives it: the shadow map is a coarse mask over a volume of thin
  // surfaces, and letting it kill the wrap made every underside in shadow flat black — the look this
  // whole path exists to remove. The direct (Lambert) term stays shadowed as three computes it.
  const capture = /* glsl */ `
		#ifdef FOLIAGE_LIGHTING
		foliageLight = directLight.color;
		#endif`;
  // WRAP LIGHTING, not transmission. The foliage mesh is a canopy BLOB with real surface normals, not
  // a sheet of leaves, and a mirrored "light from behind" lobe lit both poles — top by Lambert,
  // underside by the lobe — with the sides darkest between them. Wrap is Lambert with the terminator
  // pushed round the blob: saturate( (N·L + w) / (1 + w) ), monotonic in N·L, so top > sun-side >
  // far side > underside by construction, and no face ever exceeds a top face. Only the EXTRA over
  // Lambert is added (three already added Lambert), w = uFoliageTranslucency (0 = plain Lambert).
  const wrap = /* glsl */ `
		#ifdef FOLIAGE_LIGHTING
		{
			float foliageNdotL = dot( geometryNormal, directLight.direction );
			float foliageWrap = saturate( ( foliageNdotL + uFoliageTranslucency ) / ( 1.0 + uFoliageTranslucency ) );
			reflectedLight.directDiffuse += ( foliageWrap - saturate( foliageNdotL ) )
				* foliageLight * BRDF_Lambert( material.diffuseColor );
		}
		#endif`;
  return chunk
    .replace(declare, declare + `
		#ifdef FOLIAGE_LIGHTING
		vec3 foliageLight = vec3( 0.0 );
		#endif`)
    .replace(lightInfo, (m) => m + capture)
    // Every light type shares the call text, so the wrap term rides along with each (only
    // directional lights exist in this scene).
    .split(reDirect).join(reDirect + wrap)
    .replace(hemi, 'getHemisphereLightIrradiance( hemisphereLights[ i ], foliageHemiNormal( geometryNormal ) )');
}

// ============== TransparentDepthMaterial ==============

class TransparentDepthMaterial extends THREE.MeshDepthMaterial {
  private _shader: ShaderWithUniforms | null = null;
  private textures: LoadedTextures | null = null;

  constructor() {
    super({
      depthPacking: THREE.RGBADepthPacking,
      side: THREE.DoubleSide,
    });
    // Alpha-tested, wind-animated caster by default; the low tier turns it off (setDetail).
    this.defines = { SHADOW_CASTER_DETAIL: '' };

    this.setTextures(createDefaultPlaceholders());

    this.onBeforeCompile = (shader) => {
      this._shader = shader;

      shader.uniforms.mapArray = { value: this.textures?.albedo };
      shader.uniforms.repeatScales = { value: MATERIAL_REPEAT_SCALES };
      shader.uniforms.alphaCutoff = { value: ALPHA_CUTOFF };
      shader.uniforms.uTime = { value: 0.0 };
      shader.uniforms.uWindStrength = { value: WIND_STRENGTH };
      shader.uniforms.uWindFrequency = { value: WIND_FREQUENCY };

      // Vertex shader
      shader.vertexShader = depthVertexPrefix + shader.vertexShader;
      if (shader.vertexShader.includes('#include <skinning_vertex>')) {
        shader.vertexShader = shader.vertexShader.replace(
          '#include <skinning_vertex>',
          `#include <skinning_vertex>\n${depthVertexSuffix}`
        );
      } else if (shader.vertexShader.includes('#include <project_vertex>')) {
        shader.vertexShader = shader.vertexShader.replace(
          '#include <project_vertex>',
          `${depthVertexSuffix}\n#include <project_vertex>`
        );
      }

      // Fragment shader
      shader.fragmentShader = depthFragmentPrefix + shader.fragmentShader;
      if (shader.fragmentShader.includes('vec4 diffuseColor = vec4( 1.0 );')) {
        shader.fragmentShader = shader.fragmentShader.replace(
          'vec4 diffuseColor = vec4( 1.0 );',
          `vec4 diffuseColor = vec4( 1.0 );\n${depthAlphaDiscard}`
        );
      } else {
        shader.fragmentShader = shader.fragmentShader.replace(
          '#include <clipping_planes_fragment>',
          `#include <clipping_planes_fragment>\n${depthAlphaDiscard}`
        );
      }
    };
  }

  setTextures(textures: LoadedTextures): void {
    this.textures = textures;
    if (this._shader) {
      this._shader.uniforms.mapArray.value = textures.albedo;
      this.needsUpdate = true;
    }
  }
  
  setWindTime(time: number): void {
    if (this._shader?.uniforms.uTime) {
      this._shader.uniforms.uTime.value = time * WIND_SPEED;
    }
  }

  /** Alpha test + wind in the caster (SHADOW_CASTER_DETAIL). A define, so a change recompiles once. */
  setDetail(on: boolean): void {
    const has = this.defines?.SHADOW_CASTER_DETAIL !== undefined;
    if (has === on) return;
    if (on) this.defines = { ...this.defines, SHADOW_CASTER_DETAIL: '' };
    else { const d = { ...this.defines }; delete d.SHADOW_CASTER_DETAIL; this.defines = d; }
    this.needsUpdate = true;
  }
}

// ============== Singleton Instances ==============

let solidMaterial: TerrainMaterial | null = null;
let transparentMaterial: TerrainMaterial | null = null;
let transparentDepthMaterial: TransparentDepthMaterial | null = null;
// Note: liquidMaterial now uses WaterMaterial from WaterMaterial.ts

export function getTerrainMaterial(): TerrainMaterial {
  if (!solidMaterial) {
    solidMaterial = new TerrainMaterial(false);
    // No scene environment map is assigned, so envMapIntensity is inert — keep it at 1.
    solidMaterial.envMapIntensity = 1.0;
  }
  return solidMaterial;
}

export function getTransparentTerrainMaterial(): TerrainMaterial {
  if (!transparentMaterial) {
    transparentMaterial = new TerrainMaterial(true);
    // No scene environment map is assigned, so envMapIntensity is inert — keep it at 1.
    transparentMaterial.envMapIntensity = 1.0;
  }
  return transparentMaterial;
}

export function getLiquidTerrainMaterial(): WaterMaterial {
  return getWaterMaterial();
}

/**
 * Create a DEDICATED terrain material for the first-person held item.
 *
 * The shared world material samples its tri-planar texture by WORLD position, so a
 * camera-locked mesh's texture swims as the player moves. This variant samples by
 * OBJECT position (`vWorldPosition = position`) so the texture is static in the
 * hand — matching how the thumbnail (fixed position) looks. Seeded with the
 * currently-loaded atlas; tagged `userData.heldItem` and owned by the caller
 * (dispose on rebuild).
 */
export function createHeldItemMaterial(isTransparent: boolean): TerrainMaterial {
  const mat = new TerrainMaterial(isTransparent);
  const shared = isTransparent ? getTransparentTerrainMaterial() : getTerrainMaterial();
  const tex = shared.getTextures();
  if (tex) mat.setTextures(tex);

  // Wrap the base shader build to make the tri-planar mapping fully OBJECT-space —
  // both the sample position AND the projection normal. World-space sampling makes a
  // camera-locked mesh's texture swim (position) and stretch as it rotates (normal);
  // object-space bakes the texture to the mesh surfaces, so it renders exactly like
  // the (unrotated) thumbnail regardless of how the item is oriented in the world.
  const baseCompile = mat.onBeforeCompile;
  mat.onBeforeCompile = (shader, renderer) => {
    baseCompile(shader, renderer);
    // The terrain shader derives world position/normal in the fragment and carries no varyings for
    // them; this define brings the varyings back, holding OBJECT space (terrainVertexSuffix).
    shader.defines = shader.defines || {};
    shader.defines.TERRAIN_OBJECT_SPACE = '';
  };
  const baseKey = mat.customProgramCacheKey.bind(mat);
  mat.customProgramCacheKey = () => `${baseKey()}|held`;

  mat.userData.heldItem = true;
  return mat;
}

export function getTransparentDepthMaterial(): TransparentDepthMaterial {
  if (!transparentDepthMaterial) transparentDepthMaterial = new TransparentDepthMaterial();
  return transparentDepthMaterial;
}

/**
 * Whether foliage casts with its alpha cutouts and wind (Medium+) or as solid, still cards (the low
 * tier). Driven by the quality preset — see QualityManager.applyQuality.
 */
export function setShadowCasterDetail(on: boolean): void {
  getTransparentDepthMaterial().setDetail(on);
}

// ============== Material Management ==============

/** Set textures on all terrain materials */
function setAllMaterialTextures(textures: LoadedTextures): void {
  getTerrainMaterial().setTextures(textures);
  getTransparentTerrainMaterial().setTextures(textures);
  getWaterMaterial().setTextures(textures);
  getTransparentDepthMaterial().setTextures(textures);
}

/**
 * Initialize placeholder textures from the pallet.
 * Call this early to show colored terrain while full textures load.
 */
export function initializePlaceholderTextures(): void {
  const textures = loadPalletPlaceholders();
  setAllMaterialTextures(textures);
  console.log('Placeholder textures initialized from pallet');
}

/**
 * Initialize the material system by loading textures.
 */
export async function initializeMaterials(
  resolution: TextureResolution = 'low',
  onProgress?: (loaded: number, total: number) => void
): Promise<void> {
  const textures = await loadDataArrayTextures(resolution, onProgress);
  setAllMaterialTextures(textures);
  console.log(`Material system initialized with ${resolution} resolution`);
}

/**
 * Upgrade to high-resolution textures.
 */
export async function upgradeToHighRes(
  onProgress?: (loaded: number, total: number) => void
): Promise<void> {
  const textures = await loadDataArrayTextures('high', onProgress);
  setAllMaterialTextures(textures);
  console.log('Upgraded to high resolution textures');
}

/**
 * Check if high-res textures are cached.
 */
export async function isHighResCached(): Promise<boolean> {
  return textureCache.hasResolution('high');
}

/**
 * Set debug mode for all terrain materials.
 *
 * The LOD view is deliberately NOT pushed to the shared materials: it is painted by the per-LOD
 * variants swapped onto chunk meshes (see LodDebugMaterials), and putting the shared ones in that
 * mode would flatten everything they alone draw — the held item, the preset thumbnails — to a
 * uniform white. A chunk mesh that never got a lodTag stays textured, which is just as visible.
 */
export function setTerrainDebugMode(mode: TerrainDebugMode): void {
  const shared = mode === TERRAIN_DEBUG_MODES.LOD ? TERRAIN_DEBUG_MODES.OFF : mode;
  getTerrainMaterial().setDebugMode(shared);
  getTransparentTerrainMaterial().setDebugMode(shared);
  getLiquidTerrainMaterial().setDebugMode(shared);
}

/**
 * Update wind animation time for transparent materials.
 */
export function updateWindTime(elapsedTime: number): void {
  transparentMaterial?.setWindTime(elapsedTime);
  updateWaterTime(elapsedTime); // Update water wave animation
  transparentDepthMaterial?.setWindTime(elapsedTime);
}

/**
 * Update environment map intensity for all terrain materials.
 * Called when scene.environmentIntensity changes.
 */
export function setTerrainEnvMapIntensity(intensity: number): void {
  if (solidMaterial) {
    solidMaterial.envMapIntensity = intensity;
    solidMaterial.needsUpdate = true;
  }
  if (transparentMaterial) {
    transparentMaterial.envMapIntensity = intensity;
    transparentMaterial.needsUpdate = true;
  }
  // Update water material
  const waterMat = getWaterMaterial();
  waterMat.envMapIntensity = intensity;
  waterMat.needsUpdate = true;
}

// ============== Material Settings API ==============

/** Material settings interface matching store */
export interface MaterialSettingsUpdate {
  roughnessMultiplier?: number;
  metalnessOffset?: number;
  aoIntensity?: number;
  normalStrength?: number;
  blendSharpness?: number;
  repeatScale?: number;
  lodTexFirstLod?: number;
  lodTexPerLod?: number;
  windStrength?: number;
  windSpeed?: number;
  windFrequency?: number;
  foliageTranslucency?: number;
  foliageSkyBias?: number;
}

/**
 * Subscribe terrain materials to store material-settings changes.
 * UI just writes the store; shaders react here. Returns an unsubscribe fn.
 */
export function subscribeMaterialSettings(): () => void {
  return useGameStore.subscribe((state, prev) => {
    if (state.materialSettings !== prev.materialSettings) {
      applyMaterialSettings(state.materialSettings);
    }
  });
}

/**
 * Apply material settings to all terrain materials.
 * Called on store changes (via subscribeMaterialSettings) and after (re)loads.
 */
export function applyMaterialSettings(settings: MaterialSettingsUpdate): void {
  const materials = [solidMaterial, transparentMaterial];
  
  for (const mat of materials) {
    if (!mat) continue;
    
    if (settings.roughnessMultiplier !== undefined) {
      mat.setRoughnessMultiplier(settings.roughnessMultiplier);
    }
    if (settings.metalnessOffset !== undefined) {
      mat.setMetalnessOffset(settings.metalnessOffset);
    }
    if (settings.aoIntensity !== undefined) {
      mat.setAoIntensity(settings.aoIntensity);
    }
    if (settings.normalStrength !== undefined) {
      mat.setNormalStrength(settings.normalStrength);
    }
    if (settings.foliageTranslucency !== undefined) mat.setFoliageTranslucency(settings.foliageTranslucency);
    if (settings.foliageSkyBias !== undefined) mat.setFoliageSkyBias(settings.foliageSkyBias);
    if (settings.blendSharpness !== undefined) {
      mat.setBlendSharpness(settings.blendSharpness);
    }
    if (settings.repeatScale !== undefined) {
      mat.setRepeatScale(settings.repeatScale);
    }
    if (settings.windStrength !== undefined) {
      mat.setWindStrength(settings.windStrength);
    }
    if (settings.windSpeed !== undefined) {
      mat.setWindSpeed(settings.windSpeed);
    }
    if (settings.windFrequency !== undefined) {
      mat.setWindFrequency(settings.windFrequency);
    }
  }
  
  // Per-LOD texture scale lives in shared uniform objects, not per material.
  if (settings.lodTexFirstLod !== undefined || settings.lodTexPerLod !== undefined) {
    setTerrainLodTexScale(
      settings.lodTexFirstLod ?? TERRAIN_LOD_TEX_FIRST_LOD,
      settings.lodTexPerLod ?? TERRAIN_LOD_TEX_PER_LOD,
    );
  }

  // Water material has its own settings - wind settings don't apply
}

/**
 * Apply voxel light fill settings to all terrain materials.
 */
export function applyLightFillSettings(settings: { skyFillPower?: number; blockFillPower?: number }): void {
  const materials = [solidMaterial, transparentMaterial];
  for (const mat of materials) {
    if (!mat) continue;
    if (settings.skyFillPower !== undefined) mat.setSkyFillPower(settings.skyFillPower);
    if (settings.blockFillPower !== undefined) mat.setBlockFillPower(settings.blockFillPower);
  }
}

/**
 * Apply block-light (emitter) colour + intensity to all terrain materials (live, no remesh).
 */
export function applyBlockLightSettings(settings: { blockLightColor?: string; blockLightIntensity?: number }): void {
  const materials = [solidMaterial, transparentMaterial];
  for (const mat of materials) {
    if (!mat) continue;
    if (settings.blockLightColor !== undefined) mat.setBlockLightColor(settings.blockLightColor);
    if (settings.blockLightIntensity !== undefined) mat.setBlockLightIntensity(settings.blockLightIntensity);
  }
}

/**
 * Set repeat scale for all terrain materials.
 */
export function setTerrainRepeatScale(scale: number): void {
  solidMaterial?.setRepeatScale(scale);
  transparentMaterial?.setRepeatScale(scale);
  // Note: WaterMaterial has fixed repeat scale
}

/**
 * Per-LOD texture enlargement: levels below `firstLod` keep the authored scale, and each level from
 * there is `perLod` times larger than the one before. `perLod` of 1 turns it off.
 */
export function setTerrainLodTexScale(firstLod: number, perLod: number): void {
  uTexScaleFirstLod.value = Math.max(1, firstLod);
  uTexScalePerLod.value = Math.max(1, perLod);
}

// ============== Quality Shader Map Defines ==============

export interface ShaderMapDefines {
  normalMaps: boolean;
  aoMaps: boolean;
  metalnessMaps: boolean;
}

/**
 * Update quality-driven shader defines and force recompilation.
 * Toggling normal/AO/metalness maps skips expensive texture samples.
 */
export function setShaderMapDefines(defines: ShaderMapDefines): void {
  const changed =
    TerrainMaterial.qualityNormalMaps !== defines.normalMaps ||
    TerrainMaterial.qualityAoMaps !== defines.aoMaps ||
    TerrainMaterial.qualityMetalnessMaps !== defines.metalnessMaps;

  TerrainMaterial.qualityNormalMaps = defines.normalMaps;
  TerrainMaterial.qualityAoMaps = defines.aoMaps;
  TerrainMaterial.qualityMetalnessMaps = defines.metalnessMaps;

  if (changed) {
    // Force shader recompilation by bumping material version
    if (solidMaterial) solidMaterial.needsUpdate = true;
    if (transparentMaterial) transparentMaterial.needsUpdate = true;
    console.log(`[TerrainMaterial] Shader defines updated: normal=${defines.normalMaps}, ao=${defines.aoMaps}, metalness=${defines.metalnessMaps}`);
  }
}

/**
 * Set anisotropic filtering level on all loaded terrain textures.
 */
export function setTerrainAnisotropy(anisotropy: number): void {
  const materials = [solidMaterial, transparentMaterial];
  for (const mat of materials) {
    if (!mat || !(mat as unknown as { textures: LoadedTextures | null }).textures) continue;
    const textures = (mat as unknown as { textures: LoadedTextures }).textures;
    for (const tex of [textures.albedo, textures.normal, textures.ao, textures.roughness, textures.metalness]) {
      if (tex && tex.anisotropy !== anisotropy) {
        tex.anisotropy = anisotropy;
        tex.needsUpdate = true;
      }
    }
  }
}

// ============== Debug Console Access ==============

/** Dump diagnostic info about the current texture state */
function diagnoseTextures(): void {
  const mat = solidMaterial;
  if (!mat) {
    console.log('[diagnose] solidMaterial not created yet');
    return;
  }
  
  const textures = (mat as unknown as { textures: LoadedTextures | null }).textures;
  if (!textures) {
    console.log('[diagnose] No textures loaded');
    return;
  }
  
  const describe = (name: string, tex: THREE.DataArrayTexture) => {
    return {
      name,
      width: tex.image?.width,
      height: tex.image?.height,
      layers: tex.image?.depth,
      format: tex.format === THREE.RGBAFormat ? 'RGBA' : tex.format === THREE.RedFormat ? 'RED' : tex.format,
      internalFormat: tex.internalFormat,
      colorSpace: tex.colorSpace,
      type: tex.type === THREE.UnsignedByteType ? 'UnsignedByte' : tex.type,
      minFilter: tex.minFilter,
      magFilter: tex.magFilter,
      anisotropy: tex.anisotropy,
      generateMipmaps: tex.generateMipmaps,
      needsUpdate: tex.needsUpdate,
      version: tex.version,
    };
  };
  
  console.table([
    describe('albedo', textures.albedo),
    describe('normal', textures.normal),
    describe('ao', textures.ao),
    describe('roughness', textures.roughness),
    describe('metalness', textures.metalness),
  ]);
  
  // Shader state
  const shader = (mat as unknown as { _shader: ShaderWithUniforms | null })._shader;
  if (shader) {
    const defines = shader.defines || {};
    console.log('[diagnose] Shader defines:', defines);
    console.log('[diagnose] Shader uniforms texture versions:', {
      mapArray: (shader.uniforms.mapArray?.value as THREE.Texture)?.version,
      normalArray: (shader.uniforms.normalArray?.value as THREE.Texture)?.version,
      aoArray: (shader.uniforms.aoArray?.value as THREE.Texture)?.version,
      roughnessArray: (shader.uniforms.roughnessArray?.value as THREE.Texture)?.version,
      metalnessArray: (shader.uniforms.metalnessArray?.value as THREE.Texture)?.version,
    });
    console.log('[diagnose] Uniform texture matches this.textures?:', {
      albedo: shader.uniforms.mapArray?.value === textures.albedo,
      normal: shader.uniforms.normalArray?.value === textures.normal,
      ao: shader.uniforms.aoArray?.value === textures.ao,
      roughness: shader.uniforms.roughnessArray?.value === textures.roughness,
      metalness: shader.uniforms.metalnessArray?.value === textures.metalness,
    });
  } else {
    console.log('[diagnose] Shader not compiled yet (_shader is null)');
  }
  
  // Quality state
  console.log('[diagnose] Quality defines:', {
    normalMaps: TerrainMaterial.qualityNormalMaps,
    aoMaps: TerrainMaterial.qualityAoMaps,
    metalnessMaps: TerrainMaterial.qualityMetalnessMaps,
  });
  
  // Material state
  console.log('[diagnose] Material version:', mat.version, 'needsUpdate:', mat.needsUpdate);
}

/** Force all terrain textures to re-upload to GPU */
function forceReuploadTextures(): void {
  for (const mat of [solidMaterial, transparentMaterial]) {
    if (!mat) continue;
    const textures = (mat as unknown as { textures: LoadedTextures | null }).textures;
    if (!textures) continue;
    for (const tex of [textures.albedo, textures.normal, textures.ao, textures.roughness, textures.metalness]) {
      tex.needsUpdate = true;
    }
  }
  console.log('[forceReupload] All texture needsUpdate flags set to true');
}

/** Force a full shader recompilation on all terrain materials */
function forceRecompile(): void {
  if (solidMaterial) solidMaterial.needsUpdate = true;
  if (transparentMaterial) transparentMaterial.needsUpdate = true;
  console.log('[forceRecompile] All terrain materials marked for recompilation');
}

if (typeof window !== 'undefined') {
  (window as unknown as Record<string, unknown>).terrainDebug = {
    modes: TERRAIN_DEBUG_MODES,
    set: setTerrainDebugMode,
    off: () => setTerrainDebugMode(TERRAIN_DEBUG_MODES.OFF),
    voxelLight: () => setTerrainDebugMode(TERRAIN_DEBUG_MODES.VOXEL_LIGHT),
    albedo: () => setTerrainDebugMode(TERRAIN_DEBUG_MODES.ALBEDO),
    normal: () => setTerrainDebugMode(TERRAIN_DEBUG_MODES.NORMAL),
    ao: () => setTerrainDebugMode(TERRAIN_DEBUG_MODES.AO),
    roughness: () => setTerrainDebugMode(TERRAIN_DEBUG_MODES.ROUGHNESS),
    metalness: () => setTerrainDebugMode(TERRAIN_DEBUG_MODES.METALNESS),
    triBlend: () => setTerrainDebugMode(TERRAIN_DEBUG_MODES.TRI_BLEND),
    materialIds: () => setTerrainDebugMode(TERRAIN_DEBUG_MODES.MATERIAL_IDS),
    materialWeights: () => setTerrainDebugMode(TERRAIN_DEBUG_MODES.MATERIAL_WEIGHTS),
    worldNormal: () => setTerrainDebugMode(TERRAIN_DEBUG_MODES.WORLD_NORMAL),
    materialHue: () => setTerrainDebugMode(TERRAIN_DEBUG_MODES.MATERIAL_HUE),
    blockLight: () => setTerrainDebugMode(TERRAIN_DEBUG_MODES.BLOCK_LIGHT),
    // Diagnostic tools
    diagnose: diagnoseTextures,
    forceReupload: forceReuploadTextures,
    forceRecompile,
  };
}
